/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * TermsAccept (v1_572) — the client's one screen at /terms?token=...
 *
 * The firm's own commercial terms and the client's fee schedule, sent after
 * approval, accepted with a tick per item behind the OTP-1 gate: nothing
 * renders before a 6-digit code emailed to the invited address is verified.
 * One acceptance, ever; the server enforces it.
 */

import { useState, useEffect } from "react";

const PAGE = { minHeight: "100vh", background: "#f3f4f6", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px" };
const CARD = { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "36px 40px", width: 620, maxWidth: "96%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" };
const BTN = { width: "100%", padding: "13px 0", borderRadius: 10, border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };

export default function TermsAccept() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [ticks, setTicks] = useState({});
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(null); // 'accepted'
  const [otpGate, setOtpGate] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0);

  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);

  const otpRequestCode = async (tok) => {
    setOtpError(null);
    try {
      const r = await fetch("/api/otp-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "terms", token: tok }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.sent) { setOtpGate(g => ({ ...(g || { token: tok }), maskedEmail: d.maskedEmail || (g && g.maskedEmail) })); setOtpResendIn(30); }
      else if (!r.ok) setOtpError(d.error || "The code could not be sent. Please try again shortly.");
    } catch (_) { setOtpError("The code could not be sent. Please check your connection and try again."); }
  };

  const load = async (tok) => {
    try {
      const r = await fetch("/api/client-terms", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "data", token: tok }) });
      const d = await r.json().catch(() => null);
      if (r.status === 401 && d && d.otpRequired) { setOtpGate(g => g || { token: tok, maskedEmail: null }); otpRequestCode(tok); setLoading(false); return; }
      if (!r.ok || !d || !d.success) { setError((d && d.error) || "This request could not be loaded."); setLoading(false); return; }
      if (d.status !== "pending") setDone(d.status);
      setData({ ...d, token: tok });
      setLoading(false);
    } catch (_) { setError("This request could not be loaded. Please check your connection and try again."); setLoading(false); }
  };

  const otpVerifyCode = async () => {
    if (!otpGate || otpBusy) return;
    setOtpBusy(true); setOtpError(null);
    try {
      const r = await fetch("/api/otp-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "terms", token: otpGate.token, code: otpCode.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) { const tok = otpGate.token; setOtpGate(null); setOtpCode(""); setLoading(true); load(tok); }
      else { setOtpError(d.error || "That code did not verify. Please try again."); if (d.requestNew) setOtpCode(""); }
    } catch (_) { setOtpError("Verification failed. Please check your connection and try again."); }
    finally { setOtpBusy(false); }
  };

  const accept = async () => {
    if (accepting || !data) return;
    setAccepting(true); setError(null);
    try {
      const agreements = { ...ticks };
      const r = await fetch("/api/client-terms", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "accept", token: data.token, agreements }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) { setError(d.error || "Acceptance could not be recorded. Please try again."); setAccepting(false); return; }
      setDone("accepted");
      setAccepting(false);
    } catch (_) { setError("Acceptance could not be recorded. Please check your connection and try again."); setAccepting(false); }
  };

  useEffect(() => {
    const tok = new URLSearchParams(window.location.search).get("token") || "";
    if (!/^[a-f0-9]{32,128}$/i.test(tok)) { setError("This link is not valid."); setLoading(false); return; }
    load(tok);
  }, []);

  const allTicked = data && (data.documents || []).every(d => ticks[d.id] === true) && (!data.feeSchedule || ticks.fees === true);

  if (loading) return <div style={PAGE}><div style={CARD}><div style={{ fontSize: 14, color: "#6b7280" }}>Loading…</div></div></div>;

  if (otpGate) {
    return (
      <div style={PAGE}>
        <div style={{ ...CARD, width: 480, alignSelf: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Verify it's you</div>
          <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.65, marginBottom: 20 }}>
            A six-digit code has been emailed to {otpGate.maskedEmail ? <b>{otpGate.maskedEmail}</b> : "the address this request was sent to"}. Enter it below to view the terms and fees awaiting your acceptance.
          </div>
          <input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={e => { if (e.key === "Enter" && otpCode.length === 6) otpVerifyCode(); }} inputMode="numeric" autoComplete="one-time-code" placeholder="······"
            style={{ width: "100%", boxSizing: "border-box", fontSize: 28, letterSpacing: 12, textAlign: "center", padding: "12px 0", border: "1px solid #d1d5db", borderRadius: 10, marginBottom: 12, fontFamily: "inherit" }} />
          {otpError && <div style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>{otpError}</div>}
          <button onClick={otpVerifyCode} disabled={otpBusy || otpCode.length !== 6} style={{ ...BTN, background: otpCode.length === 6 ? "#0f766e" : "#9ca3af", color: "#fff", cursor: otpCode.length === 6 ? "pointer" : "not-allowed" }}>
            {otpBusy ? "Verifying…" : "Verify"}
          </button>
          <div style={{ marginTop: 14, fontSize: 13, color: "#6b7280", textAlign: "center" }}>
            {otpResendIn > 0 ? `You can request another code in ${otpResendIn}s` : <span onClick={() => otpRequestCode(otpGate.token)} style={{ color: "#0f766e", cursor: "pointer", textDecoration: "underline" }}>Send a new code</span>}
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return <div style={PAGE}><div style={{ ...CARD, width: 480, alignSelf: "center" }}><div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>This page could not be opened</div><div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.65 }}>{error}</div></div></div>;
  }

  const fs = data.feeSchedule;

  return (
    <div style={PAGE}>
      <div style={CARD}>
        <div style={{ fontSize: 11, color: "#0f766e", letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>{data.firmName}</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Terms &amp; Fees{data.companyName ? ` for ${data.companyName}` : ""}</div>

        {done ? (
          <div style={{ marginTop: 18, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, padding: "18px 20px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#047857", marginBottom: 6 }}>✓ Accepted</div>
            <div style={{ fontSize: 14, color: "#065f46", lineHeight: 1.65 }}>
              Your acceptance has been recorded{data.acceptedAt ? ` (${String(data.acceptedAt).replace("T", " ").slice(0, 16)} UTC)` : ""}. A certificate has been filed with {data.firmName}, and they will be in touch about next steps. You can close this page.
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.7, margin: "6px 0 22px" }}>
              {data.recipientName ? data.recipientName + ", p" : "P"}lease open and read each document, then tick each box to confirm your acceptance on behalf of {data.companyName || "your company"}.
            </div>

            {(data.documents || []).map(d => (
              <div key={d.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 10, background: ticks[d.id] ? "#f0fdfa" : "#fafafa" }}>
                <input type="checkbox" checked={ticks[d.id] === true} onChange={e => setTicks(t => ({ ...t, [d.id]: e.target.checked }))} style={{ width: 18, height: 18, marginTop: 2, accentColor: "#0f766e", cursor: "pointer" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600 }}>{d.name} <span style={{ fontWeight: 400, color: "#9ca3af", fontSize: 12 }}>version {d.version}</span></div>
                  <div style={{ fontSize: 13, color: "#4b5563", marginTop: 2 }}>
                    {d.url ? <a href={d.url} target="_blank" rel="noreferrer" style={{ color: "#0f766e" }}>Open and read the document ↗</a> : <span style={{ color: "#b91c1c" }}>The document could not be loaded. Refresh this page.</span>}
                    <span style={{ marginLeft: 8 }}>I have read and accept this document.</span>
                  </div>
                </div>
              </div>
            ))}

            {fs && (
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 10, overflow: "hidden", background: ticks.fees ? "#f0fdfa" : "#fafafa" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px" }}>
                  <input type="checkbox" checked={ticks.fees === true} onChange={e => setTicks(t => ({ ...t, fees: e.target.checked }))} style={{ width: 18, height: 18, marginTop: 2, accentColor: "#0f766e", cursor: "pointer" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 600 }}>Fee Schedule <span style={{ fontWeight: 400, color: "#9ca3af", fontSize: 12 }}>currency {fs.currency}</span></div>
                    <div style={{ fontSize: 13, color: "#4b5563", marginTop: 2 }}>
                      {data.feePdfUrl && <a href={data.feePdfUrl} target="_blank" rel="noreferrer" style={{ color: "#0f766e" }}>Open as PDF ↗</a>}
                      <span style={{ marginLeft: data.feePdfUrl ? 8 : 0 }}>I have read and accept the fee schedule below.</span>
                    </div>
                  </div>
                </div>
                <div style={{ padding: "0 16px 14px 46px" }}>
                  {fs.sections.map((sec, si) => (
                    <div key={si} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", textTransform: "uppercase", letterSpacing: 0.6, margin: "10px 0 4px" }}>{sec.title}</div>
                      {sec.items.map((it, ii) => (
                        <div key={ii} style={{ borderBottom: "1px solid #f3f4f6", padding: "6px 0" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                            <div style={{ fontSize: 13, color: "#374151" }}>{it.label}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>{it.value}</div>
                          </div>
                          {it.note && <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 1 }}>{it.note}</div>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <div style={{ fontSize: 13, color: "#b91c1c", margin: "10px 0" }}>{error}</div>}

            <button onClick={accept} disabled={!allTicked || accepting} style={{ ...BTN, marginTop: 14, background: allTicked ? "#0f766e" : "#9ca3af", color: "#fff", cursor: allTicked ? "pointer" : "not-allowed" }}>
              {accepting ? "Recording your acceptance…" : allTicked ? "Accept" : "Tick every box to accept"}
            </button>
            <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.6, marginTop: 12, textAlign: "center" }}>
              Your acceptance is recorded with the date, time and the email address it was given from (verified by code), and a certificate is filed with {data.firmName}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
